// Derive session-key onboarding: the customer's wallet is their own EOA and
// owns the Derive account directly. Two different signatures are involved and
// they authorise different things.
//
// The login header — `personal_sign` over a millisecond timestamp — is what
// Derive's private REST accepts from the wallet itself. It reads the wallet's
// own subaccounts, which is how onboarding shows the picker before any session
// key exists, and it also authenticates the registration call that the
// not-yet-registered key cannot sign for.
//
// The Action signature is `eth_signTypedData_v4` over the payload the backend
// built, and it is the one that grants trading rights — over exactly the
// subaccounts named in it. Nothing is broadcast and no gas is spent.
import { stringToHex } from 'viem'

import {
  assertManualWalletAction,
  getWalletProvider,
  switchWalletChain,
} from '@/lib/wallet/connect'
import { requireSignature, signTypedDataV4 } from '@/lib/wallet/sign-typed-data'

/** Derive's private-REST login header, as the wire carries it. */
export type DeriveLoginHeader = {
  loginTimestampMs: string
  loginSignature: string
}

export type SessionKeySignatures = DeriveLoginHeader & {
  actionSignature: string
}

const NO_SIGNATURE = 'The wallet returned no signature. Approve both prompts and try again.'

async function signLoginTimestamp(walletAddress: string): Promise<DeriveLoginHeader> {
  const provider = await getWalletProvider()
  const loginTimestampMs = Date.now().toString()
  const loginSignature = requireSignature(
    await provider.request({
      method: 'personal_sign',
      params: [stringToHex(loginTimestampMs), walletAddress as `0x${string}`],
    }),
    NO_SIGNATURE
  )
  return { loginTimestampMs, loginSignature }
}

/**
 * The login header on its own, for reading the wallet's subaccounts before it
 * has granted anything.
 *
 * This signature carries no trading rights: Derive authorises a wallet to read
 * its own subaccounts on it, and the backend's read path holds headers rather
 * than a signer, so it cannot place an order or move funds. Granting comes
 * afterwards, from the typed data, and names the subaccounts the customer
 * picked out of what this read returned.
 *
 * The backend rejects a timestamp more than five minutes old, so take it where
 * the listing is about to happen rather than caching one for a whole wizard.
 */
export async function signDeriveLogin(walletAddress: string): Promise<DeriveLoginHeader> {
  assertManualWalletAction()
  return signLoginTimestamp(walletAddress)
}

/**
 * Both signatures Derive's `private/set_session_key` needs, taken back to back.
 *
 * The login timestamp is minted after the typed-data prompt returns, not
 * before: that prompt is the slow half, and the backend rejects a timestamp
 * more than five minutes old.
 *
 * `typedDataJson` goes to the wallet verbatim. The backend recovers the signer
 * against the Action it persisted at prepare time, so rebuilding the payload
 * here could only diverge from what is actually being authorised — including
 * the subaccount list the customer is granting rights over.
 */
export async function signSessionKeyAction(params: {
  walletAddress: string
  typedDataJson: string
  chainId: number
}): Promise<SessionKeySignatures> {
  assertManualWalletAction()
  const { walletAddress, typedDataJson, chainId } = params

  await switchWalletChain(chainId)

  const actionSignature = await signTypedDataV4(walletAddress, typedDataJson, NO_SIGNATURE)

  return { actionSignature, ...(await signLoginTimestamp(walletAddress)) }
}

export { connectWallet, getChainId } from '@/lib/wallet/connect'
